<template>
  <div class="products-page">
    <div class="sidebar-wrapper" v-if="windowSize > 600">
      <sidebar
        @changeCategory="onCategoryChange"
        @changeMerchant="onMerchantChange"
        @removeCategoryFromList="onRemoveCategoryFromList"
        @shareCategory="onShareCategory"
        class="sidebar"
      />
    </div>
    <div class="products-content">
      <div class="no-items" v-if="emptyProductsState">
        <span>Use the Chrome extension to add items to your Shopdrop.</span>
        <h3 class="header-font">Try Shopdrop now!</h3>
        <div class="my-2">
          <v-btn @click="openChromeStore" color="#d5e167" large
            >Add to Chrome</v-btn
          >
        </div>
      </div>
      <div
        v-else-if="productsToShow.length"
        class="products-row"
        :class="{ 'products-row-small-screen': windowSize <= 600 }"
      >
        <Card
          :cardData="product"
          :key="i"
          @delete="onRemoveProduct"
          @share="onShareWhatsApp"
          @setProductAlarm="onSetProductAlarm"
          class="card"
          v-for="(product, i) in productsToShow"
        />
      </div>
    </div>

    <modal
      title="Delete Product"
      :showModal="isDeleteProductModalOn"
      ok="Delete"
      close="Cancel"
      @ok="onOkDeleteProductModal"
      @close="onCloseDeleteProductModal"
    >
      <div slot="content">
        <v-card-subtitle
          >Are you sure you want to delete
          {{ productToDelete.name }}?</v-card-subtitle
        >
      </div>
    </modal>

    <modal
      title="Delete Category"
      :showModal="isDeleteCategoryModalOn"
      ok="Delete"
      close="Cancel"
      @ok="onOkDeleteCategoryModal"
      @close="onCloseDeleteCategoryModal"
    >
      <div slot="content">
        <v-card-subtitle
          >Are you sure you want to delete
          {{ categoryToDelete }}?</v-card-subtitle
        >
      </div>
    </modal>

    <modal
      title="Share On WhatsApp"
      :showModal="isShareWhatsAppModalOn"
      ok="Share"
      close="Cancel"
      @ok="onOpenWhatsApp"
      @close="isShareWhatsAppModalOn = false"
    >
      <div slot="content"></div>
    </modal>

    <modal
      title="Share Product"
      :showModal="isShareModalOn"
      ok="Share"
      close="Close"
      @ok="shareProductData"
      @close="isShareModalOn = false"
    >
      <div slot="content">
        <v-card-subtitle
          >insert desired shared emails sepreated by comma, ie:
          test@gmail.com,test2@gmail.com</v-card-subtitle
        >
        <v-card-text>
          <v-text-field
            class
            color="rgb(20,200,200)"
            label="Shared Emails"
            required
            v-model="sharedEmails"
          ></v-text-field>
        </v-card-text>
      </div>
    </modal>

    <modal
      title="Create Product Alarm"
      :showModal="isProductAlarmModalOn"
      ok="Save"
      close="Close"
      @ok="saveAlarm"
      @close="isProductAlarmModalOn = false"
    >
      <div slot="content">
        <form class="form">
          <v-checkbox v-model="discountCheckbox" label="Discounts"></v-checkbox>
          <v-checkbox
            v-model="stockCheckbox"
            label="Back in stock"
          ></v-checkbox>
        </form>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import Sidebar from "./Sidebar";
import Card from "./Card";
import Modal from "./Modal";
import differenceWith from "lodash.differencewith";
import includes from "lodash.includes";
import isEmpty from "lodash.isempty";

export default {
  name: "products",
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (!isEmpty(vm.$store.getters["token"])) {
        next();
      } else {
        next({ name: "home" });
      }
    });
  },
  components: {
    Sidebar,
    Card,
    Modal,
  },
  data() {
    return {
      sharedEmails: "",
      windowSize: window.innerWidth,
      maxAppsInRow: 3,
      selectedCategories: [],
      selectedMerchants: [],
      isShareModalOn: false,
      isProductAlarmModalOn: false,
      sharedProductId: "",
      isDeleteCategoryModalOn: false,
      categoryToDelete: "",
      productToDelete: {},
      isDeleteProductModalOn: false,
      isShareWhatsAppModalOn: false,
      productToShare: {},
      alarmProduct: {},
      stockCheckbox: false,
      discountCheckbox: false,
    };
  },
  computed: {
    ...mapGetters(["token", "products", "alarmProducts"]),
    productsToShow() {
      const selectedCategories = this.selectedCategories;
      const selectedMerchants = this.selectedMerchants;

      if (isEmpty(selectedMerchants) && isEmpty(selectedCategories)) {
        return this.products;
      }

      if (!isEmpty(selectedMerchants) && isEmpty(selectedCategories)) {
        return this.products.filter((product) =>
          includes(selectedMerchants, product.merchantUrl)
        );
      }

      if (isEmpty(selectedMerchants) && !isEmpty(selectedCategories)) {
        return this.products.filter(
          (product) =>
            product.category &&
            differenceWith(
              product.category,
              selectedCategories,
              (productCategory, category) => productCategory === category
            ).length < product.category.length
        );
      }

      return this.products.filter(
        (product) =>
          includes(selectedMerchants, product.merchantUrl) &&
          product.category &&
          differenceWith(
            product.category,
            selectedCategories,
            (productCategory, category) => productCategory === category
          ).length < product.category.length
      );
    },
    emptyProductsState() {
      return this.products.length === 0;
    },
  },
  methods: {
    ...mapActions([
      "removeProduct",
      "shareProduct",
      "removeCategory",
      "addBanner",
      "addProductAlarm",
    ]),
    onCategoryChange(selectedCategories) {
      this.selectedCategories = selectedCategories;
    },
    onMerchantChange(selectedMerchants) {
      this.selectedMerchants = selectedMerchants;
    },
    async shareProductData(product) {
      try {
        const emails = this.sharedEmails.split(",");
        await this.shareProduct({
          productId: this.sharedProductId,
          emails,
        });
        const banner = {
          type: "success",
          text: `Product has been shared with ${JSON.stringify(
            emails.length
          )} reviewers.`,
          dismiss: true,
        };
        this.addBanner(banner);
        this.isShareModalOn = false;
        this.sharedProductId = "";
        this.sharedEmails = "";
      } catch {
        const banner = {
          type: "error",
          text: "Oops something went wrong..please try again.",
          dismiss: true,
        };
        this.addBanner(banner);
        this.isShareModalOn = false;
        this.sharedProductId = "";
        this.sharedEmails = "";
      }
    },
    async onShareProduct(productId) {
      this.isShareModalOn = true;
      this.sharedProductId = productId;
      this.sharedEmails = "";
    },
    openChromeStore() {
      window.open(
        "https://chrome.google.com/webstore/detail/dropshop/gemeodelaigfdiekcialemndbakgdicl?hl=en&authuser=2",
        "_blank"
      );
    },
    async onOkDeleteCategoryModal(category) {
      try {
        this.isDeleteCategoryModalOn = false;
        await this.removeCategory(this.categoryToDelete);

        const banner = {
          type: "success",
          text: "Category has been removed.",
          dismiss: true,
        };
        this.addBanner(banner);
        this.categoryToDelete = "";
      } catch (err) {
        const banner = {
          type: "error",
          text: "Oops something went wrong..please try again.",
          dismiss: true,
        };
        this.addBanner(banner);
        this.categoryToDelete = "";
      }
    },
    async onOpenWhatsApp() {
      this.isShareWhatsAppModalOn = false;
      if (this.windowSize > 600) {
        window.open(
          `https://web.whatsapp.com/send?text=https://getshopdrop.com/sharing?productIds=${encodeURIComponent(
            this.productToShare.productId
          )}`
        );
      } else {
        window.open(
          `https://wa.me/send?text=https://getshopdrop.com/sharing?productIds=${encodeURIComponent(
            this.productToShare.productId
          )}`
        );
      }
    },

    async onCopyProductLink() {
      //   this.$copyText(this.productToShare.url).then(
      //     (e) => {
      //       alert(JSON.stringify(e));
      //       console.log(e);
      //     },
      //     (e) => {
      //       alert("Can not copy");
      //       console.log(e);
      //     }
      //   );

      this.isShareWhatsAppModalOn = false;

      const input = document.getElementById("toClipboard");
      input.value = this.productToShare.url;
      input.select();
      document.execCommand("copy");
      //   const banner = {
      //     type: "success",
      //     text: "Product link copied to clipboard.",
      //     dismiss: true,
      //   };
      //   this.addBanner(banner);
    },
    onShareCategory(productIds) {
      this.isShareWhatsAppModalOn = true;
      const products = {
        productId: productIds,
      };
      this.productToShare = products;
    },
    async onCloseDeleteCategoryModal() {
      this.isDeleteCategoryModalOn = false;
      this.categoryToDelete = "";
    },
    onRemoveCategoryFromList(category) {
      this.isDeleteCategoryModalOn = true;
      this.categoryToDelete = category;
    },
    async onOkDeleteProductModal() {
      try {
        await this.removeProduct(this.productToDelete.id);
        const banner = {
          type: "success",
          text: "Product has been removed.",
          dismiss: true,
        };
        this.addBanner(banner);
        this.isDeleteProductModalOn = false;
        this.productToDelete = {};
      } catch {
        const banner = {
          type: "error",
          text: "Oops something went wrong..please try again.",
          dismiss: true,
        };
        this.addBanner(banner);
        this.isDeleteProductModalOn = false;
        this.productToDelete = {};
      }
    },

    async onCloseDeleteProductModal() {
      this.isDeleteProductModalOn = false;
      this.productToDelete = {};
    },

    onRemoveProduct(product) {
      this.isDeleteProductModalOn = true;
      this.productToDelete = product;
    },
    onShareWhatsApp(product) {
      this.isShareWhatsAppModalOn = true;
      this.productToShare = product;
    },
    saveAlarm() {
      this.isProductAlarmModalOn = false;
      if (this.stockCheckbox || this.discountCheckbox) {
        this.addProductAlarm(this.alarmProduct);
        this.stockCheckbox = false;
        this.discountCheckbox = false;
      }
    },
    onSetProductAlarm(alarmProduct) {
      this.alarmProduct = alarmProduct;
      this.isProductAlarmModalOn = true;
    },
  },
  watch: {
    token(newVal) {
      if (!newVal) {
        this.$router.push({ name: "home" });
      }
    },
  },
};
</script>

<style scoped>
.sidebar-wrapper {
  display: flex;
  flex-direction: column;
}

.sidebar {
  flex: 1;
}

.products-page {
  height: 100%;
  display: flex;
}

.products-content {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.no-items {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
}

.products-row-small-screen {
  justify-content: center !important;
}
.products-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
}

.card {
  flex: 1 0 25%;
  margin-right: 0.5%;
  margin-left: 0.5%;
  margin-top: 1%;
  margin-bottom: 1%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
}

.spacer {
  max-width: 400px;
  margin: 0 0 24px 18px;
  padding: 20px 24px 24px 24px;
}

.to-clipboard {
  border: 0;
}
.form {
  padding: 5%;
}
</style>
